/* The Catalyst · Quantum Cocktails recipe app
   Shopify section script. Everything runs inside <qc-catalyst> elements;
   nothing touches the rest of the theme. */
(() => {
  if (customElements.get('qc-catalyst')) return;

  const SYRUPS = [
    { id: 'simple', name: 'Simple Syrup', short: 'Simple', desc: 'The backbone of the classics: clean sweetness that lets the spirit and citrus do the talking.' },
    { id: 'grenadine', name: 'Grenadine', short: 'Grenadine', desc: 'Tart fruit and a ruby glow, not just colour.' },
    { id: 'orgeat', name: 'Orgeat', short: 'Orgeat', desc: 'Almond richness with a hint of floral aroma: the secret behind the Mai Tai and a silky texture in sours.' },
    { id: 'falernum', name: 'Falernum', short: 'Falernum', desc: 'The Caribbean’s spice cabinet in a bottle: warm spice and lime that make rum drinks taste like the islands.' },
    { id: 'cinnamon', name: 'Cinnamon', short: 'Cinnamon', desc: 'Warm, woody spice that turns a familiar drink into a cozy one, and a key part of Don the Beachcomber’s tiki legends.' },
    { id: 'strawberry', name: 'Strawberry-Lime', short: 'Strawberry-Lime', desc: 'Bright summer fruit with a citrus snap. It slots into almost any sour or highball in place of plain syrup.' }
  ];
  const SY = Object.fromEntries(SYRUPS.map((s) => [s.id, s]));
  const BASES = [
    ['all', 'All'], ['rum', 'Rum'], ['whisky', 'Whisky & rye'], ['gin', 'Gin'], ['tequila', 'Tequila'],
    ['brandy', 'Brandy & cognac'], ['vodka', 'Vodka'], ['sparkling', 'Sparkling wine'],
    ['other', 'Chartreuse, pastis & bitters'], ['zero', 'Zero-proof']
  ];

  const store = {
    get(k, d) { try { const v = localStorage.getItem('qc:' + k); return v === null ? d : JSON.parse(v); } catch (e) { return d; } },
    set(k, v) { try { localStorage.setItem('qc:' + k, JSON.stringify(v)); } catch (e) { /* storage unavailable */ } }
  };
  const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
  const dot = (id) => `<i class="qc-dot" style="--qc-c:var(--qc-c-${id})" aria-hidden="true"></i>`;
  const dots = (ids) => `<span class="qc-dots">${ids.map(dot).join('')}</span>`;
  const slug = (n) => n.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[’']/g, '').replace(/&/g, 'and').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const fmt = (n) => (Math.round(n * 100) / 100).toString();
  const toMl = (oz) => { const ml = oz * 30; return ml < 10 ? Math.round(ml * 2) / 2 : Math.ceil(ml - 0.5); };

  function bottleSVG(id) {
    return `<svg viewBox="0 0 40 92" aria-hidden="true" focusable="false">
      <rect class="qc-cap" x="13" y="1" width="14" height="10" rx="2"/>
      <path class="qc-liquid" fill="var(--qc-c-${id})" d="M7 46c0-4 2-7 4-9h18c2 2 4 5 4 9v38a5 5 0 0 1-5 5H12a5 5 0 0 1-5-5z"/>
      <path class="qc-glass" d="M15 11h10v9c0 6 10 9 10 20v44a6 6 0 0 1-6 6H11a6 6 0 0 1-6-6V40c0-11 10-14 10-20z"/>
      <rect class="qc-label" x="7" y="52" width="26" height="22" rx="2"/>
      <text class="qc-qc" x="20" y="66" text-anchor="middle">QC</text>
    </svg>`;
  }

  class QcCatalyst extends HTMLElement {
    connectedCallback() {
      if (!this._ready) this.init();
      // Window-level listeners are tied to this instance and removed on disconnect,
      // so theme-editor section reloads never stack duplicate handlers.
      this._abort = new AbortController();
      window.addEventListener('hashchange', () => this.route(), { signal: this._abort.signal });
      this.route();
    }

    disconnectedCallback() {
      if (this._abort) this._abort.abort();
      if (this.dlg && this.dlg.open) this.dlg.close();
    }

    init() {
      this._ready = true;
      const dataEl = this.querySelector('script[data-qc-recipes]');
      let recipes = [];
      try { recipes = JSON.parse(dataEl.textContent); } catch (e) { console.error('The Catalyst: recipe data missing', e); }
      const base = this.dataset.assetBase || '';
      this.recipes = recipes.map((r) => {
        const id = slug(r.name);
        const serves = /serves (\d+)/i.exec(r.sub || '');
        return Object.assign({}, r, {
          id,
          src: base + r.img,
          serves: serves ? +serves[1] : 1,
          search: [r.name, r.sub || '', r.ings.join(' '), r.glass, r.garnish].join(' ').toLowerCase()
        });
      });
      this.text = {
        tally0: this.dataset.tallyNone || '{total} recipes in the book',
        tally: this.dataset.tallySome || '{n} of {total} recipes use only syrups on your shelf',
        tallyAll: this.dataset.tallyAll || 'All {total} recipes use only syrups on your shelf',
        togetherHeading: this.dataset.togetherHeading || 'Better Together',
        togetherIntro: this.dataset.togetherIntro || '',
        moreHeading: this.dataset.moreHeading || 'Add one more bottle',
        moreIntro: this.dataset.moreIntro || ''
      };
      this.state = {
        shelf: new Set(store.get('shelf', [])),
        saved: new Set(store.get('saved', [])),
        units: store.get('units', 'oz'),
        base: 'all', q: '', savedOnly: false
      };
      this.cur = null; this.mult = 1;

      this.el = (sel) => this.querySelector(sel);
      this.bottlesEl = this.el('[data-qc-bottles]');
      this.chipsEl = this.el('[data-qc-chips]');
      this.listEl = this.el('[data-qc-list]');
      this.dlg = this.el('[data-qc-detail]');
      this.searchEl = this.el('[data-qc-search]');
      this.savedBtn = this.el('[data-qc-saved-only]');

      this.bottlesEl.innerHTML = SYRUPS.map((s) => `
        <button type="button" class="qc-bottle" data-s="${s.id}" aria-pressed="false">
          ${bottleSVG(s.id)}<span>${s.short}</span>
        </button>`).join('');
      this.chipsEl.innerHTML = BASES.map(([id, l]) => `<button type="button" class="qc-chip" data-b="${id}" aria-pressed="${id === 'all'}">${esc(l)}</button>`).join('');

      this.bottlesEl.addEventListener('click', (e) => {
        const b = e.target.closest('.qc-bottle'); if (!b) return;
        const id = b.dataset.s, sh = this.state.shelf;
        sh.has(id) ? sh.delete(id) : sh.add(id);
        this.saveShelf(); this.render();
      });
      this.el('[data-qc-all-six]').addEventListener('click', () => { SYRUPS.forEach((s) => this.state.shelf.add(s.id)); this.saveShelf(); this.render(); });
      this.el('[data-qc-clear]').addEventListener('click', () => { this.state.shelf.clear(); this.saveShelf(); this.render(); });
      this.chipsEl.addEventListener('click', (e) => {
        const c = e.target.closest('.qc-chip'); if (!c) return;
        this.state.base = c.dataset.b;
        this.chipsEl.querySelectorAll('.qc-chip').forEach((x) => x.setAttribute('aria-pressed', x === c));
        this.render();
      });
      this.searchEl.addEventListener('input', (e) => { this.state.q = e.target.value.trim().toLowerCase(); this.render(); });
      this.savedBtn.addEventListener('click', () => {
        this.state.savedOnly = !this.state.savedOnly;
        this.savedBtn.setAttribute('aria-pressed', this.state.savedOnly);
        this.render();
      });
      this.listEl.addEventListener('click', (e) => {
        const d = e.target.closest('[data-id],[data-go]'); if (!d) return;
        if (d.matches('[data-qc-reset]')) return;
        this.go(d.dataset.id || d.dataset.go);
      });
      this.querySelectorAll('[data-qc-view]').forEach((b) => b.addEventListener('click', () => {
        if (b.dataset.qcView === 'foreword') location.hash = '#/foreword';
        else this.clearHash();
        this.showView(b.dataset.qcView);
        this.scrollIntoView({ block: 'start' });
      }));
      this.dlg.addEventListener('cancel', (e) => { e.preventDefault(); this.closeDrink(); });
      this.dlg.addEventListener('click', (e) => { if (e.target === this.dlg) this.closeDrink(); });

      this.render();
    }

    saveShelf() { store.set('shelf', [...this.state.shelf]); }
    canMake(r) { return r.syrups.every((s) => this.state.shelf.has(s)); }
    missing(r) { return r.syrups.filter((s) => !this.state.shelf.has(s)); }
    tileVar(r) { return r.syrups.length > 1 ? 'together' : r.syrups[0]; }
    go(id) { location.hash = '#/drink/' + id; }
    clearHash() {
      if (location.hash.startsWith('#/')) history.pushState(null, '', location.pathname + location.search);
    }

    passes(r) {
      const s = this.state;
      if (s.base === 'zero' && !r.zero) return false;
      if (s.base !== 'all' && s.base !== 'zero' && !r.spirits.includes(s.base)) return false;
      if (s.savedOnly && !s.saved.has(r.id)) return false;
      if (s.q && !s.q.split(/\s+/).every((w) => r.search.includes(w))) return false;
      return true;
    }

    card(r) {
      return `<button type="button" class="qc-drink" data-id="${r.id}" style="--qc-tile:var(--qc-tile-${this.tileVar(r)});--qc-c:var(--qc-c-${r.syrups[0]})">
        <span class="qc-tile">
          <img class="${r.photo ? 'qc-photo' : ''}" src="${esc(r.src)}" alt="" loading="lazy" width="420" height="525">
          ${r.zero ? '<span class="qc-flag">Zero-proof</span>' : ''}
          ${this.state.saved.has(r.id) ? '<span class="qc-fav" aria-label="Saved">★</span>' : ''}
        </span>
        <span class="qc-nm">${esc(r.name)}</span>
        <span class="qc-meta">${dots(r.syrups)}<span>${esc(cap(r.glass.split(/,| or /)[0]))}</span></span>
      </button>`;
    }

    chapter(title, desc, colorVar, items) {
      if (!items.length) return '';
      return `<section class="qc-chapter">
        <div class="qc-chapter-head" style="--qc-c:${colorVar}">
          <span class="qc-swatch"></span><h3>${esc(title)}</h3>${desc ? `<p>${esc(desc)}</p>` : ''}
        </div>
        <div class="qc-grid">${items.join('')}</div>
      </section>`;
    }

    render() {
      const s = this.state, R = this.recipes, total = R.length;
      this.bottlesEl.querySelectorAll('.qc-bottle').forEach((b) => b.setAttribute('aria-pressed', s.shelf.has(b.dataset.s)));
      const shelfOn = s.shelf.size > 0;
      const makeable = R.filter((r) => this.canMake(r));
      const fill = (t) => t.replace('{n}', makeable.length).replace('{total}', total);
      this.el('[data-qc-tally]').textContent = !shelfOn ? fill(this.text.tally0)
        : makeable.length === total ? fill(this.text.tallyAll) : fill(this.text.tally);
      this.el('[data-qc-clear]').hidden = !shelfOn;
      this.el('[data-qc-all-six]').hidden = s.shelf.size === 6;

      const pool = (shelfOn ? makeable : R).filter((r) => this.passes(r));
      let html = '';
      SYRUPS.forEach((sy) => {
        const items = pool.filter((r) => r.syrups.length === 1 && r.syrups[0] === sy.id).map((r) => this.card(r));
        html += this.chapter(sy.name, sy.desc, `var(--qc-c-${sy.id})`, items);
      });
      html += this.chapter(this.text.togetherHeading, this.text.togetherIntro, 'var(--qc-gold)', pool.filter((r) => r.syrups.length > 1).map((r) => this.card(r)));

      if (!pool.length) {
        const msg = s.savedOnly && !s.saved.size
          ? 'Open any recipe and tap Save to keep it here.'
          : 'Try a different search or base spirit, or add another syrup to your shelf.';
        html = `<div class="qc-empty"><h3>No recipes match yet</h3><p>${msg}</p><button type="button" class="qc-btn qc-ghost" data-qc-reset>Reset search and filters</button></div>`;
      }

      if (shelfOn && s.shelf.size < 6) {
        const rows = SYRUPS.filter((sy) => !s.shelf.has(sy.id)).map((sy) => ({
          sy, gain: R.filter((r) => { const m = this.missing(r); return m.length === 1 && m[0] === sy.id; }).filter((r) => this.passes(r))
        })).filter((x) => x.gain.length).sort((a, b) => b.gain.length - a.gain.length);
        if (rows.length) {
          html += `<section class="qc-chapter"><div class="qc-chapter-head" style="--qc-c:var(--qc-gold)"><span class="qc-swatch"></span><h3>${esc(this.text.moreHeading)}</h3>${this.text.moreIntro ? `<p>${esc(this.text.moreIntro)}</p>` : ''}</div>`
            + rows.map(({ sy, gain }) => `<div class="qc-unlock"><div class="qc-who">${dot(sy.id)}<div><b>${esc(sy.name)}</b><small>${gain.length} more ${gain.length === 1 ? 'recipe' : 'recipes'}</small></div></div><div class="qc-names">${gain.map((r) => `<button type="button" data-go="${r.id}">${esc(r.name)}</button>`).join('')}</div></div>`).join('')
            + '</section>';
        }
      }
      this.listEl.innerHTML = html;
      const reset = this.listEl.querySelector('[data-qc-reset]');
      if (reset) reset.addEventListener('click', () => {
        s.q = ''; s.base = 'all'; s.savedOnly = false;
        this.searchEl.value = ''; this.savedBtn.setAttribute('aria-pressed', 'false');
        this.chipsEl.querySelectorAll('.qc-chip').forEach((x) => x.setAttribute('aria-pressed', x.dataset.b === 'all'));
        this.render();
      });
    }

    qty(line) {
      const m = line.match(/^(\d+(?:\.\d+)?)\s+(.*)$/);
      if (!m) return ['', line];
      const n = parseFloat(m[1]) * this.mult; let rest = m[2];
      if (/^oz\b/.test(rest)) {
        rest = rest.replace(/^oz\s*/, '');
        return [this.state.units === 'ml' ? `${toMl(n)} mL` : `${fmt(n)} oz`, rest];
      }
      if (n !== 1) rest = rest.replace(/^dash\b/, 'dashes').replace(/^drop\b/, 'drops').replace(/^egg white\b/, 'egg whites');
      const u = rest.match(/^(tsp|dashes|dash|drops|drop)\b\s*/);
      if (u) return [`${fmt(n)} ${u[1]}`, rest.slice(u[0].length)];
      return [fmt(n), rest];
    }

    ingHTML(line) {
      const [q, rest] = this.qty(line);
      let text = esc(rest);
      if (/QC/.test(rest)) {
        const ids = /each QC/.test(rest) ? SYRUPS.map((s) => s.id)
          : SYRUPS.filter((s) => rest.includes('QC ' + s.short) || rest.includes('QC ' + s.name)).map((s) => s.id);
        text = `<span class="qc-qcline">${dots(ids)}<span>${text}</span></span>`;
      }
      return `<li><span class="qc-q">${q}</span><span>${text}</span></li>`;
    }

    openDrink(id) {
      const r = this.recipes.find((x) => x.id === id); if (!r) return;
      if (this.cur !== r) this.mult = 1;
      this.cur = r;
      const s = this.state, dlg = this.dlg, nameId = this.id + '-name';
      const others = r.syrups.length === 1
        ? this.recipes.filter((x) => x !== r && x.syrups.length > 1 && x.syrups.includes(r.syrups[0])) : [];
      dlg.setAttribute('aria-labelledby', nameId);
      dlg.innerHTML = `<div class="qc-sheet" style="--qc-tile:var(--qc-tile-${this.tileVar(r)})">
        <div class="qc-art">
          <button type="button" class="qc-close" data-qc-close aria-label="Close">×</button>
          <img class="${r.photo ? 'qc-photo' : ''}" src="${esc(r.src)}" alt="${esc(r.name)}">
        </div>
        <div class="qc-body">
          <div class="qc-syrup-tags">${r.syrups.map((x) => `<span>${dot(x)}QC ${esc(SY[x].name)}</span>`).join('')}</div>
          <h2 id="${nameId}">${esc(r.name)}</h2>
          ${r.sub ? `<p class="qc-sub">${esc(r.sub)}</p>` : ''}
          ${r.zero ? '<p class="qc-sub">Zero-proof</p>' : ''}
          <div class="qc-controls">
            <div class="qc-stepper">
              <button type="button" data-qc-minus aria-label="Fewer servings">−</button>
              <output data-qc-serves aria-live="polite"></output>
              <button type="button" data-qc-plus aria-label="More servings">+</button>
            </div>
            <div class="qc-seg" role="group" aria-label="Units">
              <button type="button" data-u="oz" aria-pressed="${s.units === 'oz'}">oz</button>
              <button type="button" data-u="ml" aria-pressed="${s.units === 'ml'}">mL</button>
            </div>
          </div>
          <ul class="qc-ings" data-qc-ings></ul>
          <h4>Method</h4>
          <p class="qc-method">${esc(r.method)}</p>
          <dl class="qc-serve"><dt>Glass</dt><dd>${esc(cap(r.glass))}</dd><dt>Garnish</dt><dd>${esc(cap(r.garnish))}</dd></dl>
          ${r.note ? `<p class="qc-note">${esc(r.note)}</p>` : ''}
          ${others.length ? `<h4>Better together</h4><div class="qc-pairs">${others.map((o) => `<button type="button" data-go="${o.id}">${dots(o.syrups)}${esc(o.name)}</button>`).join('')}</div>` : ''}
          <div class="qc-actions"><button type="button" class="qc-btn" data-qc-save></button></div>
        </div>
      </div>`;
      this.paintIngs(); this.paintSave();
      dlg.querySelector('[data-qc-close]').addEventListener('click', () => this.closeDrink());
      dlg.querySelector('[data-qc-minus]').addEventListener('click', () => { if (this.mult > 1) { this.mult--; this.paintIngs(); } });
      dlg.querySelector('[data-qc-plus]').addEventListener('click', () => { if (this.mult < 12) { this.mult++; this.paintIngs(); } });
      dlg.querySelectorAll('.qc-seg button').forEach((b) => b.addEventListener('click', () => {
        s.units = b.dataset.u; store.set('units', s.units);
        dlg.querySelectorAll('.qc-seg button').forEach((x) => x.setAttribute('aria-pressed', x === b));
        this.paintIngs();
      }));
      dlg.querySelector('[data-qc-save]').addEventListener('click', () => {
        s.saved.has(r.id) ? s.saved.delete(r.id) : s.saved.add(r.id);
        store.set('saved', [...s.saved]); this.paintSave(); this.render();
      });
      dlg.querySelectorAll('[data-go]').forEach((b) => b.addEventListener('click', () => this.go(b.dataset.go)));
      if (!dlg.open) dlg.showModal();
      const body = dlg.querySelector('.qc-body'); if (body) body.scrollTop = 0;
    }

    paintIngs() {
      const n = this.cur.serves * this.mult;
      this.dlg.querySelector('[data-qc-serves]').textContent = `${n} ${n === 1 ? 'drink' : 'drinks'}`;
      this.dlg.querySelector('[data-qc-ings]').innerHTML = this.cur.ings.map((l) => this.ingHTML(l)).join('');
    }

    paintSave() {
      const b = this.dlg.querySelector('[data-qc-save]'), on = this.state.saved.has(this.cur.id);
      b.textContent = on ? '★ Saved' : '☆ Save this recipe';
      b.className = on ? 'qc-btn' : 'qc-btn qc-ghost';
      b.setAttribute('aria-pressed', on);
    }

    closeDrink() {
      if (location.hash.startsWith('#/drink/')) this.clearHash();
      if (this.dlg.open) this.dlg.close();
    }

    showView(v) {
      this.el('[data-qc-panel="recipes"]').hidden = v !== 'recipes';
      this.el('[data-qc-panel="foreword"]').hidden = v !== 'foreword';
      this.querySelectorAll('[data-qc-view]').forEach((b) => b.setAttribute('aria-selected', b.dataset.qcView === v));
    }

    route() {
      const h = location.hash;
      if (h.startsWith('#/drink/')) { this.showView('recipes'); this.openDrink(decodeURIComponent(h.slice(8))); }
      else if (h === '#/foreword') { if (this.dlg.open) this.dlg.close(); this.showView('foreword'); }
      else { if (this.dlg.open) this.dlg.close(); this.showView('recipes'); }
    }
  }

  customElements.define('qc-catalyst', QcCatalyst);
})();
